/*
 * Copyright (c) 2024 Via Transportation, Inc. All rights reserved.
 *
 * This software is distributed under license and may not be used, copied,
 * modified, or distributed without the express written permission of
 * Via Transportation, Inc.
 */
import { useRef, useState, useEffect } from "react";
import { ViaButton, ViaLoader, ViaTextfield, ViaTypography } from "via-react-design-system";
import { FormattedMessage } from "react-intl";

import { useClasses } from "./styles";

interface ChallengeProps {
    userPhoneNumber: string;
    loading: boolean;
    isError: boolean;
    onSubmit: (code: string) => void;
    onProblemsReceivingCodeClick: () => void;
}
export const Challenge = ({
    userPhoneNumber,
    loading,
    isError,
    onSubmit,
    onProblemsReceivingCodeClick,
}: ChallengeProps) => {
    const [values, setValues] = useState(["", "", "", "", "", ""]);
    const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

    const classes = useClasses();

    useEffect(() => {
        if (!isError) {
            setValues(["", "", "", "", "", ""]);
        }
    }, [isError]);

    const handleChange = (e, index) => {
        const newValues = [...values];
        newValues[index] = e.target.value;

        if (e.target.value.length === 1 && index < 5) {
            inputsRef.current[index + 1]?.focus();
        }

        setValues(newValues);

        if (newValues.every((val) => val.length === 1)) {
            onSubmit(newValues.join(""));
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === "Backspace" && values[index] === "" && index > 0) {
            inputsRef.current[index - 1]?.focus();
        }
    };

    return (
        <div>
            <ViaTypography variant="display.semi-strong" dataTestId="enter-code-title">
                <FormattedMessage
                    defaultMessage="Enter your code"
                    id="application.login.enterCode"
                />
            </ViaTypography>
            <ViaTypography variant="body.regular" dataTestId="message-was-sent-message">
                <FormattedMessage
                    defaultMessage="It was sent to {userPhoneNumber}. Please type it here."
                    id="application.login.codeWasSentToPhoneNumber"
                    values={{ userPhoneNumber }}
                />
            </ViaTypography>
            <div className={classes.fields} data-test-id='fields-container'>
                {values.map((value, index) => (
                    <ViaTextfield
                        key={index}
                        value={value}
                        onChange={(e) => handleChange(e, index)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        inputRef={(el) => (inputsRef.current[index] = el)}
                        maxLength={1}
                        className={classes.codeField}
                        inputClassName={classes.codeInputField}
                        width={52}
                        type="number"
                        showNumberButtons={false}
                        disabled={loading}
                        error={isError}
                        dataTestId={`field-${value}`}
                    />
                ))}
            </div>
            <ViaTypography variant="body.semi-strong" dataTestId="it-may-take-time-to-retrieve-sms">
                <FormattedMessage
                    defaultMessage="It may take a few minutes to receive the code, Please retry after 60 seconds."
                    id="application.login.itMayTakeUpToSixtySecondsToGetSms"
                />
            </ViaTypography>
            <div className={classes.buttonWrapper}>
                <ViaButton
                    variant="ghost"
                    width="fullWidth"
                    disabled={loading}
                    onClick={onProblemsReceivingCodeClick}
                    dataTestId='problem-receiving-code-button'
                >
                    <ViaTypography variant="button-label.regular">
                        <FormattedMessage
                            defaultMessage="Problems receiving code?"
                            id="application.login.problemsReceivingCode"
                        />
                    </ViaTypography>
                </ViaButton>
            </div>
            <div className={classes.loadingWrapper} data-testid="loader-wrapper">{loading && <ViaLoader size="small" />}</div>
        </div>
    );
};
